import { ActionIcon, Button, Group, rem, Table, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconCircleCheck, IconCircleX, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { OptInUserFormProvider, useOptInUserFormContext } from "shared/components/opt-in/context";
import { VerifyEudPanel } from "shared/components/opt-in/VerifyEudPanel";
import { useFormState } from "shared/hooks/useFormState";
import { useOptInUserMutation } from "shared/hooks/useGraphql";
import { fail, runAsync } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";

export const ContactMethodsPanel = () => {
    const nav = useNavigate();

    const form = useOptInUserFormContext();
    const [userId] = useFormState(form, "id");
    const [euds] = useFormState(form, "euds");

    const sendVerificationCode = useOptInUserMutation(
        graphql(`
            mutation OptInSendVerificationCode($userId: UUID!, $eudId: UUID!) {
                resendEUDVerificationCode(userId: $userId, eudId: $eudId) {
                    id
                }
            }
        `),
    );
    const deleteEud = useOptInUserMutation(
        graphql(`
            mutation OptInDeleteEUD($userId: UUID!, $eudId: UUID!) {
                removeEUD(userId: $userId, eudId: $eudId)
            }
        `),
    );

    return (
        <>
            <div>
                <Title order={4}>Contact Methods</Title>
                <Text size="sm">Add at least one contact method where you wish to be alerted to complete signup.</Text>
            </div>

            <Table.ScrollContainer minWidth={"100%"}>
                <Table withTableBorder striped verticalSpacing="md">
                    <Table.Thead>
                        <Table.Th w={rem(30)}>Verified</Table.Th>
                        <Table.Th>Contact</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th></Table.Th>
                    </Table.Thead>
                    <Table.Tbody>
                        {euds.length == 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={4} ta="center" bg="gray-accent">
                                    <Text size="xs">No Contact Methods</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {euds.map((each) => (
                            <Table.Tr key={each.id}>
                                <Table.Td ta="center">
                                    {!each.isVerified ? (
                                        <ThemeIcon variant="transparent" c="red">
                                            <IconCircleX />
                                        </ThemeIcon>
                                    ) : (
                                        <ThemeIcon variant="transparent" c="green">
                                            <IconCircleCheck />
                                        </ThemeIcon>
                                    )}
                                </Table.Td>
                                <Table.Td>{each.value}</Table.Td>
                                <Table.Td>{each.type}</Table.Td>
                                <Table.Td>
                                    <Group align="center" gap="xs" wrap="nowrap" justify="flex-end">
                                        {!each.isVerified && (
                                            <Button
                                                variant="default"
                                                size="compact-sm"
                                                onClick={() => {
                                                    if (!userId) {
                                                        fail("User ID not found");
                                                        return;
                                                    }
                                                    sendVerificationCode.mutate({ userId, eudId: each.id });
                                                    modals.open({
                                                        title: <Title order={4}>Verify Contact Method</Title>,
                                                        children: (
                                                            <OptInUserFormProvider form={form}>
                                                                <VerifyEudPanel
                                                                    eudId={each.id}
                                                                    type={each.type}
                                                                    onConflict={() => {
                                                                        modals.closeAll();
                                                                        void notify
                                                                            .confirm(
                                                                                "This contact method is associated with an existing user and will receive alerts. To manage this user please log in or create an account.",
                                                                                {
                                                                                    label: "Continue to Login",
                                                                                    title: "Existing Contact Method",
                                                                                },
                                                                            )
                                                                            .then(({ close }) => close())
                                                                            .then(() => nav({ to: "/opt-in" }));
                                                                    }}
                                                                />
                                                            </OptInUserFormProvider>
                                                        ),
                                                    });
                                                }}
                                            >
                                                Verify
                                            </Button>
                                        )}

                                        <Tooltip
                                            label="Cannot remove only verified device"
                                            disabled={!each.isVerified || euds.filter((it) => it.isVerified).length > 1}
                                        >
                                            <ActionIcon
                                                variant="transparent"
                                                onClick={() =>
                                                    runAsync(async () => {
                                                        const { close } = await notify.confirm(
                                                            "Are you sure you want to delete this contact method?",
                                                            {
                                                                title: "Delete Contact Method",
                                                                label: "Delete",
                                                                color: "red",
                                                            },
                                                        );
                                                        if (!userId) {
                                                            fail("User Id not found");
                                                            return;
                                                        }

                                                        await deleteEud.mutateAsync({ userId, eudId: each.id });
                                                        form.setFieldValue("euds", (prev) => prev.filter((it) => it.id != each.id));
                                                        close();
                                                    })
                                                }
                                                disabled={each.isVerified && euds.filter((it) => it.isVerified).length == 1}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        </>
    );
};
